import { useCallback, useEffect, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import { buildLifeCheckSummary } from '@/lib/life-check';
import {
  dismissLifeCheckPrompt,
  dismissLifeCheckResurfacePrompt,
} from '@/lib/lifeCheckPrompt';
import { trackProductEvent } from '@/lib/product-analytics';
import type { LifeCheckAnswers, LifeCheckRecord, LifeCheckSummary } from '@/types';

function mapLifeCheck(row: {
  id: string;
  answers: unknown;
  summary: unknown;
  created_at: string;
  updated_at: string;
}): LifeCheckRecord {
  return {
    id: row.id,
    answers: row.answers as LifeCheckAnswers,
    summary: row.summary as LifeCheckSummary,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function useLifeCheck() {
  const { user } = useApp();
  const [latestLifeCheck, setLatestLifeCheck] = useState<LifeCheckRecord | null>(null);
  const [lifeCheckHistory, setLifeCheckHistory] = useState<LifeCheckRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLatestLifeCheck = useCallback(async () => {
    if (!user) {
      setLatestLifeCheck(null);
      setLifeCheckHistory([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from('life_checks')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(5);

    if (error) {
      console.error('Failed to fetch life check:', error);
      setLatestLifeCheck(null);
      setLifeCheckHistory([]);
      setLoading(false);
      return;
    }

    const records = (data || []).map(mapLifeCheck);
    setLifeCheckHistory(records);
    setLatestLifeCheck(records[0] || null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchLatestLifeCheck();
  }, [fetchLatestLifeCheck]);

  const saveLifeCheck = useCallback(async (answers: LifeCheckAnswers) => {
    if (!user) throw new Error('You must be signed in to save a Life Check.');

    const summary = buildLifeCheckSummary(answers);
    const { data, error } = await supabase
      .from('life_checks')
      .insert({
        user_id: user.id,
        answers: answers as unknown as Record<string, unknown>,
        summary: summary as unknown as Record<string, unknown>,
      })
      .select('*')
      .single();

    if (error) {
      throw error;
    }

    const record = mapLifeCheck(data);
    setLatestLifeCheck(record);
    setLifeCheckHistory((current) => [record, ...current.filter((item) => item.id !== record.id)].slice(0, 5));
    dismissLifeCheckPrompt(user.id);
    dismissLifeCheckResurfacePrompt(user.id);
    window.dispatchEvent(new CustomEvent('adulting:data-changed'));
    void trackProductEvent({
      userId: user.id,
      eventName: 'life_check_completed',
      path: '/life-check',
      metadata: {
        workSituation: answers.workSituation,
        housingSituation: answers.housingSituation,
        responsibilities: answers.responsibilities,
        monthlyIncomeBand: answers.monthlyIncomeBand,
        supportTypes: answers.supportTypes,
        estimatedImpactMin: summary.estimatedImpactMin ?? null,
        estimatedImpactMax: summary.estimatedImpactMax ?? null,
      },
    });
    return record;
  }, [user]);

  return {
    latestLifeCheck,
    lifeCheckHistory,
    loading,
    saveLifeCheck,
    refreshLifeCheck: fetchLatestLifeCheck,
  };
}
